import { useEffect, useState } from "react";
import { Loading } from "@/components/Loading";
import { useParams } from "react-router-dom";
import { StatusPill, QUOTE_STATUS_TONES } from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ApiError } from "@/lib/apiClient";
import { publicInvoices, type PublicInvoice } from "@/features/quotes/api/quotes.api";
import { formatDateTime } from "@/utils/dates";

export default function InvoiceViewPage() {
  const { token } = useParams<{ token: string }>();
  const [inv, setInv] = useState<PublicInvoice | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setError("Missing invoice token.");
      setLoading(false);
      return;
    }
    let cancelled = false;
    publicInvoices.get(token)
      .then((data) => {
        if (cancelled) return;
        setInv(data);
        setLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        if (e instanceof ApiError && e.status === 404) {
          setError("This invoice is no longer available.");
        } else {
          setError(e instanceof Error ? e.message : "Failed to load invoice.");
        }
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <Loading>Loading invoice…</Loading>
      </div>
    );
  }

  if (error || !inv) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center px-4">
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-8 max-w-md w-full text-center">
          <h1 className="text-lg font-semibold text-slate-800 mb-2">
            Invoice not available
          </h1>
          <p className="text-sm text-slate-600">
            {error ?? "We couldn't find this invoice. The link may be invalid or expired."}
          </p>
        </div>
      </div>
    );
  }

  const issuedDate = formatDateTime(inv.issuedAt, {
    year: "numeric", month: "long", day: "numeric",
  });

  return (
    <div className="min-h-screen bg-slate-100 py-8 px-4 print:bg-white print:py-0">
      <div className="max-w-3xl mx-auto bg-white border border-slate-200 rounded-lg shadow-sm p-8 print:border-0 print:shadow-none">
        <div className="flex items-start justify-between mb-8 pb-6 border-b border-slate-200">
          <div>
            <div className="text-xl font-semibold text-slate-900">{inv.companyName}</div>
            {inv.companyEmail && <div className="text-sm text-slate-600 mt-1">{inv.companyEmail}</div>}
            {inv.companyAddress && (
              <div className="text-sm text-slate-600 whitespace-pre-line mt-1">{inv.companyAddress}</div>
            )}
          </div>
          <div className="text-right">
            <div className="text-2xl font-semibold text-slate-900">INVOICE</div>
            <div className="text-sm text-slate-600 mt-1">{inv.displayId}</div>
            <div className="text-xs text-slate-500 mt-2">Issued {issuedDate}</div>
            <StatusPill value={inv.status} map={QUOTE_STATUS_TONES} className="mt-2" />
          </div>
        </div>

        <div className="mb-8">
          <div className="text-xs font-medium uppercase tracking-wide text-slate-500 mb-1">Bill to</div>
          <div className="text-sm text-slate-800 font-medium">
            {inv.customerCompany ? `${inv.customerCompany} — ${inv.customerName}` : inv.customerName}
          </div>
          {inv.customerEmail && <div className="text-sm text-slate-600">{inv.customerEmail}</div>}
          {inv.customerAddress && (
            <div className="text-sm text-slate-600 whitespace-pre-line">{inv.customerAddress}</div>
          )}
        </div>

        <div className="mb-6">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="font-medium text-slate-700">Description</TableHead>
                <TableHead className="text-right font-medium text-slate-700 w-24">Qty</TableHead>
                <TableHead className="text-right font-medium text-slate-700 w-32">Unit price</TableHead>
                <TableHead className="text-right font-medium text-slate-700 w-32">Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {inv.items.map((line, i) => (
                <TableRow key={i}>
                  <TableCell className="text-slate-800">{line.description}</TableCell>
                  <TableCell className="text-right tabular-nums text-slate-700">{line.quantity}</TableCell>
                  <TableCell className="text-right tabular-nums text-slate-700">{money(line.unitPrice, inv.currency)}</TableCell>
                  <TableCell className="text-right tabular-nums text-slate-800">{money(line.lineTotal, inv.currency)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <div className="ml-auto max-w-xs space-y-1 text-sm">
          <TotalRow label="Subtotal" value={inv.subtotal} currency={inv.currency} />
          {inv.discountPct > 0 && (
            <TotalRow label={`Discount (${(inv.discountPct * 100).toFixed(2)}%)`} value={-(inv.subtotal * inv.discountPct)} currency={inv.currency} />
          )}
          {inv.discountAmount > 0 && (
            <TotalRow label="Discount" value={-inv.discountAmount} currency={inv.currency} />
          )}
          {inv.taxRate > 0 && (
            <TotalRow label={`Tax (${(inv.taxRate * 100).toFixed(3)}%)`} value={inv.taxAmount} currency={inv.currency} />
          )}
          {inv.shippingCharge > 0 && (
            <TotalRow label="Shipping" value={inv.shippingCharge} currency={inv.currency} />
          )}
          <div className="border-t border-slate-300 pt-1 mt-1">
            <TotalRow label="Total" value={inv.totalAmount} currency={inv.currency} bold />
          </div>
        </div>

        {inv.notes && (
          <div className="mt-8 pt-6 border-t border-slate-200">
            <div className="text-xs font-medium uppercase tracking-wide text-slate-500 mb-1">Notes</div>
            <div className="text-sm text-slate-700 whitespace-pre-line">{inv.notes}</div>
          </div>
        )}

        <div className="mt-8 pt-6 border-t border-slate-200 flex items-center justify-between print:hidden">
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" onClick={() => window.print()}>
              Print
            </Button>
            {token && (
              <Button asChild>
                <a href={publicInvoices.pdfUrl(token)} target="_blank" rel="noopener noreferrer">
                  Download PDF
                </a>
              </Button>
            )}
          </div>
          <span className="text-xs text-slate-500">Questions? Reply to the email you received.</span>
        </div>
      </div>
    </div>
  );
}

function TotalRow({ label, value, currency, bold = false }: {
  label: string; value: number; currency: string; bold?: boolean;
}) {
  return (
    <div className={`flex justify-between ${bold ? "font-semibold text-slate-900 text-base" : "text-slate-600"}`}>
      <span>{label}</span>
      <span className="tabular-nums">{money(value, currency)}</span>
    </div>
  );
}

function money(n: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(n);
  } catch {
    return `${currency} ${n.toFixed(2)}`;
  }
}
